import React from 'react';
import { Button, Card, CardContent, Typography, Box, IconButton, Chip, Snackbar, Alert, Stack, Divider } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ShareIcon from '@mui/icons-material/Share';
import LockIcon from '@mui/icons-material/Lock';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { Event } from '../types/Event';

interface Props {
  event: Event;
  onJoin: (event: Event) => void;
  onManage: (event: Event) => void;
  onTournament?: (event: Event) => void;
  onDuplicate?: (event: Event) => void;
  isAuthenticated: boolean;
  isAdmin?: boolean;
  siteSettings?: {
    whatsappMessageTemplate?: string;
    defaultSenderId?: string;
  };
}

const EventButton: React.FC<Props> = ({ event, onJoin, onManage, onTournament, onDuplicate, isAuthenticated, isAdmin = false, siteSettings }) => {
  const [showCopiedMessage, setShowCopiedMessage] = React.useState(false);

  const shareDirectLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Get the route from the event.site object
    const route = typeof event.site === 'object' && event.site.route 
      ? event.site.route 
      : window.location.pathname.substring(1);
    
    const directLink = `${window.location.origin}/${route}/event/${event._id}`;
    
    try {
      await navigator.clipboard.writeText(directLink);
      setShowCopiedMessage(true);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = directLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopiedMessage(true);
    }
  };

  const handleCloseCopiedMessage = () => {
    setShowCopiedMessage(false);
  };

  const shareToWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Get the route from the event.site object
    const route = typeof event.site === 'object' && event.site.route 
      ? event.site.route 
      : window.location.pathname.substring(1);
    
    const directEventLink = `https://matchpoint.hk/${route}/event/${event._id}`;
    
    let message = encodeURIComponent(
      `查詢以下活動\n\n日期：${event.date}\n時間：${event.time}\n地點：${event.location}`
    );
    
    if (isAdmin) {
      // Use custom template if available, otherwise use default
      if (siteSettings?.whatsappMessageTemplate) {
        // Replace placeholders with actual values
        let customMessage = siteSettings.whatsappMessageTemplate
          .replace('{date}', formatDate(event.date))
          .replace('{dayOfWeek}', getDayOfWeek(event.date))
          .replace('{time}', event.time)
          .replace('{location}', event.location)
          .replace('{details}', event.otherDetails || '')
          .replace('{maxParticipants}', event.maxParticipants.toString())
          .replace('{fee}', event.splitFee ? `平分` : `$${event.memberFee}`)
          .replace('{route}', directEventLink);
          
        message = encodeURIComponent(customMessage);
      } else {
        // Use default template with direct event link
        message = encodeURIComponent(
          `日期: ${formatDate(event.date)} (${getDayOfWeek(event.date)})\n` +
          `時間: ${event.time}\n` +
          `地點: ${event.location}\n` +
          `用球: RSL Supreme\n` +
          `級別: ${event.otherDetails}\n` +
          `人數: ${event.maxParticipants}人\n` +
          `費用: ${event.splitFee ? `平分` : `$${event.memberFee}`}\n\n` +
          `報名請PM\n` +
          `或自助報名: ${directEventLink}`
        );
      }
    }
    
    // Use custom phone number if available
    const phoneNumber = siteSettings?.defaultSenderId || '85255053400';
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const handleManage = (e: React.MouseEvent) => {
    e.stopPropagation();
    onManage(event);
  };

  const handleTournament = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTournament) {
      onTournament(event);
    }
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDuplicate) {
      onDuplicate(event);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const getDayOfWeek = (dateString: string): string => {
    const daysOfWeek = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    const date = new Date(dateString);
    return daysOfWeek[date.getDay()];
  };

  const getDay = (dateString: string): string => {
    const date = new Date(dateString);
    return date.getDate().toString();
  };

  const getMonth = (dateString: string): string => {
    const date = new Date(dateString);
    return (date.getMonth() + 1).toString() + '月';
  };
  
  // Check if event is in the past
  const isPastEvent = (): boolean => {
    const timeComponents = event.time.split(' - ');
    const endTimeString = timeComponents.length > 1 ? timeComponents[1] : timeComponents[0];
    
    const [hours, minutes] = endTimeString.split(':').map(Number);
    const eventDate = new Date(event.date);
    eventDate.setHours(hours, minutes, 0, 0);
    
    return eventDate < new Date();
  };

  // Check if all participants are paid
  const areAllParticipantsPaid = (): boolean => {
    if (!isAdmin || !event.participants || event.participants.length === 0) {
      return true;
    }
    return event.participants.every(participant => participant.paid);
  };
  
  const participantCount = event.participants?.reduce((sum, p) => sum + (p.spots ?? 0), 0) ?? 0;
  const availableSpots = event.maxParticipants - participantCount;
  const isFull = availableSpots <= 0;
  const isPast = isPastEvent();

  // Status Color Logic
  const getStatusColor = () => {
    if (isPast) return 'grey.500';
    if (isFull) return 'error.main';
    return 'success.main';
  };

  return (
    <>
      <Card 
        elevation={0}
        onClick={() => onJoin(event)}
        sx={{
          borderRadius: 3,
          transition: 'all 0.2s',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3,
            borderColor: 'transparent'
          },
          cursor: 'pointer',
          position: 'relative',
          overflow: 'visible',
          opacity: isPast ? 0.8 : 1
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Date Box */}
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 76,
                minWidth: 76,
                height: 76,
                bgcolor: isPast ? 'action.hover' : '#EFF6FF', // Very light blue
                borderRadius: 3,
                border: '1px solid',
                borderColor: isPast ? 'transparent' : '#DBEAFE', // Light blue border
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: isPast ? 'text.disabled' : '#6B7280' }}>
                {getMonth(event.date)}
              </Typography>
              <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1, color: isPast ? 'text.disabled' : '#1a237e' }}>
                {getDay(event.date)}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: isPast ? 'text.disabled' : '#6B7280' }}>
                {getDayOfWeek(event.date).replace('星期', '週')}
              </Typography>
            </Box>

            {/* Event Details */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {event.type === 'tournament' && (
                      <Chip 
                        icon={<EmojiEventsIcon sx={{ fontSize: '0.9rem !important' }} />}
                        label="比賽" 
                        size="small" 
                        color="secondary"
                        variant="outlined" 
                        sx={{ height: 20, fontSize: '0.7rem' }} 
                      />
                    )}
                    {event.isPrivate && (
                      <Chip 
                        icon={<LockIcon sx={{ fontSize: '0.9rem !important' }} />}
                        label="私人" 
                        size="small" 
                        variant="outlined" 
                        sx={{ height: 20, fontSize: '0.7rem' }} 
                      />
                    )}
                    {isAdmin && !areAllParticipantsPaid() && (
                      <Chip 
                        label="未全付" 
                        size="small" 
                        color="warning" 
                        sx={{ height: 20, fontSize: '0.7rem' }} 
                      />
                    )}
                  </Box>
                  <Typography variant="subtitle1" fontWeight="bold" noWrap>
                    {event.location}
                  </Typography>
                </Box>
                
                {/* Actions */}
                <Box sx={{ display: 'flex', ml: 1 }}>
                  <IconButton size="small" onClick={shareToWhatsApp}>
                    <WhatsAppIcon fontSize="small" color="success" />
                  </IconButton>
                  <IconButton size="small" onClick={shareDirectLink}>
                    <ShareIcon fontSize="small" color="action" />
                  </IconButton>
                  {event.type === 'tournament' && (
                    <IconButton size="small" onClick={handleTournament}>
                      <EmojiEventsIcon fontSize="small" color="secondary" />
                    </IconButton>
                  )}
                  {isAdmin && (
                    <>
                      {onDuplicate && (
                        <IconButton size="small" onClick={handleDuplicate} aria-label="複製活動">
                          <ContentCopyIcon fontSize="small" color="action" />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={handleManage}>
                        <EditIcon fontSize="small" color="primary" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>

              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                  <AccessTimeIcon sx={{ fontSize: 16 }} />
                  <Typography variant="body2">{event.time}</Typography>
                </Box>
                {event.otherDetails && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {event.otherDetails}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Footer Stats & Action */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getStatusColor() }}>
              <GroupsIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2" fontWeight="600">
                {participantCount}/{event.maxParticipants}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={event.splitFee ? `平分` : `$${event.memberFee}`} 
                size="small" 
                color="primary" 
                variant={isPast ? "outlined" : "filled"}
                sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600 }} 
              />

              {!isPast && !isFull && (
                <Button 
                  variant="contained" 
                  disableElevation
                  startIcon={<PersonAddIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onJoin(event);
                  }}
                  sx={{ 
                    borderRadius: 50,
                    textTransform: 'none',
                    bgcolor: '#10b981', // Emerald 500
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    px: 3,
                    py: 0.75,
                    boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3), 0 2px 4px -1px rgba(16, 185, 129, 0.1)',
                    '&:hover': { 
                      bgcolor: '#059669', // Emerald 600
                      boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3), 0 4px 6px -2px rgba(16, 185, 129, 0.1)'
                    }
                  }}
                >
                  立即報名
                </Button>
              )}
              
              {isFull && !isPast && (
                <Chip label="已額滿" color="error" size="small" variant="outlined" />
              )}
              
              {isPast && (
                <Chip label="已結束" size="small" variant="outlined" />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      <Snackbar 
        open={showCopiedMessage} 
        autoHideDuration={3000} 
        onClose={handleCloseCopiedMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ 
          position: 'fixed',
          top: 'calc(24px + env(safe-area-inset-top))',
          right: 'calc(24px + env(safe-area-inset-right))',
          zIndex: 9999
        }}
      >
        <Alert onClose={handleCloseCopiedMessage} severity="success" sx={{ width: '100%' }}>
          連結已複製！
        </Alert>
      </Snackbar>
    </>
  );
};

export default EventButton;
